import { useEffect, useRef, useState } from 'react';
import { Alert, Button, Spin } from 'antd';
import { useNavigate } from 'react-router-dom';
import AxiosUtils from 'utils/axios-utils';
import { API } from 'service/api-route';
import { AuthLayout, authStyles } from 'layouts/auth';
import { getRoleValues, hasAdminAccess, resetCurrentUserCache } from 'hooks/use-current-roles';
import { getAuthSuccess } from 'redux/auth/reducers';
import { useAppDisPatch } from 'utils/common';
import { StorageKey, StorageUtils } from 'utils/local-storage';
import { setSession } from 'utils/session';

/**
 * /sso#code=… — đích của "Tôi → Trang quản trị" trong app. Đổi mã một lần (sống
 * 60 giây, do app xin ở POST /auth/admin-handoff) lấy phiên đăng nhập của trình
 * duyệt này, rồi vào thẳng Trang quản trị — không nhập mật khẩu.
 *
 * Mã nằm sau dấu # (không gửi lên máy chủ web, không vào lịch sử Referer) và bị
 * xoá khỏi thanh địa chỉ ngay khi đọc, để F5 hay chia sẻ link không lộ mã.
 */
const SsoPage = () => {
  const navigate = useNavigate();
  const dispatch = useAppDisPatch();
  const [error, setError] = useState<string | null>(null);
  const started = useRef(false);

  useEffect(() => {
    // StrictMode chạy effect hai lần; mã chỉ dùng được một lần.
    if (started.current) return;
    started.current = true;

    const hash = new URLSearchParams(window.location.hash.replace(/^#/, ''));
    const code = hash.get('code') || '';
    window.history.replaceState(null, '', window.location.pathname);

    (async () => {
      if (!code) {
        setError('Thiếu mã đăng nhập. Mở lại Trang quản trị từ app (Tôi → Trang quản trị).');
        return;
      }
      let to = '/';
      try {
        const res: any = await AxiosUtils.post(API.adminHandoffRedeem, { code });
        to = typeof res?.data?.to === 'string' && res.data.to.startsWith('/') ? res.data.to : '/';
      } catch (err: any) {
        setError(
          (typeof err?.error === 'string' && err.error) ||
            'Liên kết đã hết hạn hoặc đã được dùng. Mở lại Trang quản trị từ app.'
        );
        return;
      }
      try {
        const roles = getRoleValues(await AxiosUtils.get(API.getUserInfo));
        if (!hasAdminAccess(roles)) {
          setError('Tài khoản này không có quyền vào Trang quản trị.');
          return;
        }
      } catch {
        setError('Không kiểm tra được quyền của tài khoản. Vui lòng thử lại.');
        return;
      }
      // Giống đăng nhập bằng mật khẩu (redux/auth/actions.ts → login).
      resetCurrentUserCache();
      setSession({ accessToken: 'OK' } as any);
      dispatch(getAuthSuccess({ accessToken: 'OK' }));
      navigate(to, { replace: true });
    })();
  }, [dispatch, navigate]);

  const clearAndLogin = () => {
    try {
      StorageUtils.remove(StorageKey.SESSION);
    } catch {
      // bỏ qua
    }
    navigate('/login', { replace: true });
  };

  return (
    <AuthLayout
      title={error ? 'Chưa mở được Trang quản trị' : 'Đang mở Trang quản trị…'}
      subtitle={error ? undefined : 'Đăng nhập bằng phiên của app HP Media, không cần mật khẩu.'}
    >
      {error ? (
        <>
          <Alert type="warning" showIcon message={error} className={authStyles.formAlert} />
          <Button type="primary" size="large" block onClick={clearAndLogin}>
            Đăng nhập bằng email và mật khẩu
          </Button>
        </>
      ) : (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '24px 0' }}>
          <Spin size="large" />
        </div>
      )}
    </AuthLayout>
  );
};

export default SsoPage;
